import { Column, ColumnDef, flexRender, getCoreRowModel, getSortedRowModel, Row, RowData, type SortingState, useReactTable,type VisibilityState } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import * as React from "react";
import { cn } from "src/common/utils";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/ui/table";

declare module "@tanstack/table-core" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    formatCell?: (value: unknown, row: TData) => React.ReactNode;
    cellClassName?: string;
    headerClassName?: string;
  }
}

export interface SimpleDataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  className?: string;
  getRowId?: (row: TData, index: number) => string;
  onRowClick?: (row: TData) => void;
  enableSorting?: boolean;
  enableCellFormatting?: boolean;
  renderRowActions?: (row: Row<TData>) => React.ReactNode;
  emptyState?: React.ReactNode;
  appearance?: "clean" | "lined";
  initialSorting?: SortingState;
  columnVisibility?: VisibilityState;
}

// Skip row click when the user interacts with controls nested inside the row
// (buttons, links, inputs) so we don't fire both the nested action and the
// row navigation.
const INTERACTIVE_ROW_CLICK_SELECTOR =
  "button, a, input, textarea, select, [role='button'], [data-no-row-click='true']";

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
}: {
  column: Column<TData, TValue>;
  title: string;
  className?: string;
}) {
  if (!column.getCanSort()) {
    return <div className={cn("flex items-center text-muted-foreground", className)}>{title}</div>;
  }

  const sortedState = column.getIsSorted();

  return (
    <button
      type="button"
      className={cn(
        "-ml-2 flex h-8 items-center gap-1.5 rounded-md px-2 text-sm font-medium text-muted-foreground",
        "transition-colors hover:bg-card hover:text-foreground",
        className,
      )}
      onClick={() => column.toggleSorting(sortedState === "asc")}
    >
      <span>{title}</span>
      {sortedState === "asc" ? (
        <ArrowUp className="h-4 w-4" />
      ) : sortedState === "desc" ? (
        <ArrowDown className="h-4 w-4" />
      ) : (
        <ArrowUpDown className="h-4 w-4 opacity-50" />
      )}
    </button>
  );
}

export function SimpleDataTable<TData>({
  data,
  columns,
  className,
  getRowId,
  onRowClick,
  enableSorting = false,
  enableCellFormatting = false,
  renderRowActions,
  emptyState,
  appearance = "clean",
  initialSorting = [],
  columnVisibility,
}: SimpleDataTableProps<TData>) {
  const [sorting, setSorting] = React.useState<SortingState>(initialSorting);
  const isLined = appearance === "lined";

  const handleRowActivate = React.useCallback(
    (event: React.SyntheticEvent, row: TData) => {
      if (!onRowClick) return;
      const element = event.target as HTMLElement | null;
      const rowElement = event.currentTarget as HTMLElement;
      // Check if the click target (or an ancestor between it and the row) is an
      // interactive element. We stop at the row itself so that the row's own
      // role="button" doesn't falsely match.
      let el = element;
      while (el && el !== rowElement) {
        if (el.matches(INTERACTIVE_ROW_CLICK_SELECTOR)) return;
        el = el.parentElement;
      }
      onRowClick(row);
    },
    [onRowClick]
  );

  const processedColumns = React.useMemo(() => {
    if (!enableSorting) {
      return columns;
    }

    return columns.map((column) => {
      if (typeof column.header !== "string") {
        return column;
      }

      const title = column.header;

      return {
        ...column,
        header: ({ column: headerColumn }) => (
          <DataTableColumnHeader column={headerColumn} title={title} />
        ),
      } as ColumnDef<TData, unknown>;
    });
  }, [columns, enableSorting]);

  const finalColumns = React.useMemo(() => {
    if (!renderRowActions) {
      return processedColumns;
    }

    const actionColumn: ColumnDef<TData, unknown> = {
      id: "row-actions",
      header: () => (
        <div className="flex justify-end">
          <span className="sr-only">Actions</span>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex justify-end">{renderRowActions(row)}</div>
      ),
      enableSorting: false,
      meta: {
        cellClassName: "text-right",
        headerClassName: "text-right",
      },
    };

    return [...processedColumns, actionColumn];
  }, [processedColumns, renderRowActions]);

  const table = useReactTable({
    data,
    columns: finalColumns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      ...(enableSorting ? { sorting } : {}),
      ...(columnVisibility ? { columnVisibility } : {}),
    },
    ...(enableSorting
      ? {
          onSortingChange: setSorting,
          getSortedRowModel: getSortedRowModel(),
        }
      : {}),
    getRowId,
  });

  const rows = table.getRowModel().rows;
  const columnCount = table.getVisibleLeafColumns().length || table.getAllLeafColumns().length;

  const tableElement = (
    <Table className="bg-transparent">
      <TableHeader
        className={cn(
          "[&_tr]:border-b",
          isLined ? "[&_tr]:border-border/50" : "[&_tr]:border-border/50",
        )}
      >
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id} className={cn("hover:bg-transparent", isLined ? "border-border/50" : "border-border/50")}>
            {headerGroup.headers.map((header) => {
              const columnMeta = header.column.columnDef.meta;

              return (
                <TableHead key={header.id} className={cn("px-2 text-muted-foreground", columnMeta?.headerClassName)}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {rows.length ? (
          rows.map((row) => (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() ? "selected" : undefined}
              onClick={onRowClick ? (event) => handleRowActivate(event, row.original) : undefined}
              role={onRowClick ? "button" : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={
                onRowClick
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleRowActivate(event, row.original);
                      }
                    }
                  : undefined
              }
              className={cn(
                "hover:bg-muted/50",
                onRowClick && "cursor-pointer",
                isLined ? "border-b border-border/50 last:border-0" : "border-b border-border/50 last:border-0",
              )}
            >
              {row.getVisibleCells().map((cell) => {
                const columnMeta = cell.column.columnDef.meta;
                const formattedContent =
                  enableCellFormatting && columnMeta?.formatCell
                    ? columnMeta.formatCell(cell.getValue(), row.original)
                    : flexRender(cell.column.columnDef.cell, cell.getContext());

                return (
                  <TableCell key={cell.id} className={cn(columnMeta?.cellClassName)}>
                    {formattedContent}
                  </TableCell>
                );
              })}
            </TableRow>
          ))
        ) : (
          <TableRow className={cn(isLined ? "border-0" : undefined, "hover:bg-transparent")}
          >
            <TableCell colSpan={Math.max(columnCount, 1)} className="h-24 text-center">
              {emptyState ?? "No results"}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  return (
    <div className={cn("w-full", className)}>
      {isLined ? (
        <div className="rounded-md border border-border/50 bg-transparent">{tableElement}</div>
      ) : (
        tableElement
      )}
    </div>
  );
}
